import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { doc, setDoc, deleteDoc, updateDoc, increment, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { UserData } from '../../contexts/AuthContext';
import AuthContext from '../../contexts/AuthContext';
import OptimizedImage from '../common/OptimizedImage';

interface UserCardProps {
  user: UserData;
}

const UserCard: React.FC<UserCardProps> = ({ user }) => {
  const { currentUser } = useContext(AuthContext);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggleFollow = async () => {
    if (!currentUser) return;
    if (currentUser.uid === user.uid) return; // No puedes seguirte a ti mismo
    
    setLoading(true);
    try {
      const currentUserRef = doc(db, 'users', currentUser.uid);
      const targetUserRef = doc(db, 'users', user.uid);
      
      // Referencia al documento de seguimiento
      const followingRef = doc(db, `users/${currentUser.uid}/following`, user.uid);
      const followerRef = doc(db, `users/${user.uid}/followers`, currentUser.uid);
      
      if (isFollowing) {
        // Dejar de seguir
        await Promise.all([
          // Eliminar de la lista de seguidos del usuario actual
          deleteDoc(followingRef),
          // Eliminar de la lista de seguidores del usuario objetivo
          deleteDoc(followerRef),
          // Actualizar contadores
          updateDoc(currentUserRef, { following: increment(-1) }),
          updateDoc(targetUserRef, { followers: increment(-1) })
        ]);
        
        // Eliminar la notificación de seguimiento si existe
        try {
          const notificationQuery = query(
            collection(db, 'notifications'),
            where('fromUserId', '==', currentUser.uid),
            where('toUserId', '==', user.uid),
            where('type', '==', 'follow')
          );
          
          const notificationSnapshot = await getDocs(notificationQuery);
          notificationSnapshot.forEach(async (doc) => {
            await deleteDoc(doc.ref);
          });
        } catch (notifError) {
          console.error('Error al eliminar notificación:', notifError);
        }
      } else {
        // Seguir
        await Promise.all([
          // Añadir a la lista de seguidos del usuario actual
          setDoc(followingRef, { 
            uid: user.uid,
            displayName: user.displayName,
            photoURL: user.photoURL,
            createdAt: serverTimestamp() 
          }),
          // Añadir a la lista de seguidores del usuario objetivo
          setDoc(followerRef, { 
            uid: currentUser.uid,
            displayName: currentUser.displayName,
            photoURL: currentUser.photoURL,
            createdAt: serverTimestamp() 
          }),
          // Actualizar contadores
          updateDoc(currentUserRef, { following: increment(1) }),
          updateDoc(targetUserRef, { followers: increment(1) })
        ]);
        
        // Crear notificación para el usuario seguido
        try {
          const notificationRef = doc(collection(db, 'notifications'));
          await setDoc(notificationRef, {
            type: 'follow',
            fromUserId: currentUser.uid,
            fromUserName: currentUser.displayName,
            fromUserPhoto: currentUser.photoURL || '',
            toUserId: user.uid,
            read: false,
            createdAt: serverTimestamp()
          });
        } catch (notifError) {
          console.error('Error al crear notificación:', notifError);
        }
      }
      
      // Actualizar estado local
      setIsFollowing(!isFollowing);
    } catch (error) {
      console.error('Error toggling follow status:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
      <Link to={`/profile/${user.uid}`} className="flex items-center space-x-3">
        <OptimizedImage 
          src={user.photoURL || '/default-avatar.png'} 
          alt={user.displayName} 
          className="w-12 h-12 rounded-full"
          width={48}
          height={48}
        />
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white">{user.displayName}</h3>
          {user.bio && (
            <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">{user.bio}</p>
          )}
        </div>
      </Link>
      
      {currentUser && currentUser.uid !== user.uid && (
        <button
          onClick={toggleFollow}
          disabled={loading}
          className={`px-4 py-1.5 rounded-full text-sm font-medium ${
            isFollowing
              ? 'bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
              : 'bg-domired-600 text-white hover:bg-domired-700'
          } transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500 disabled:opacity-50`}
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Cargando...
            </span>
          ) : isFollowing ? 'Dejar de seguir' : 'Seguir'}
        </button>
      )}
    </div>
  );
};

export default UserCard;
